/**
 * /admin/campaigns/:id — campaign read view + state-machine controls.
 *
 * Polls every 5s while status=sending so the progress bar updates
 * without manual refresh. Stops polling once status becomes terminal.
 *
 * State-machine button visibility:
 *   draft     → Approve, Delete
 *   approved  → Hold, Cancel
 *   held      → Resume, Cancel
 *   sending   → (no actions — wait it out)
 *   sent, cancelled, failed → (no actions)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Campaign,
  CampaignStatus,
  approveCampaign,
  cancelCampaign,
  decodeAudienceSpec,
  decodePayload,
  deleteCampaign,
  getCampaign,
  holdCampaign,
  resumeCampaign,
} from "../../../api/campaigns";

const STATUS_BADGE: Record<CampaignStatus, string> = {
  draft: "bg-gray-200 text-gray-700",
  approved: "bg-blue-100 text-blue-700",
  sending: "bg-yellow-100 text-yellow-700 animate-pulse",
  sent: "bg-green-100 text-green-700",
  held: "bg-orange-100 text-orange-700",
  cancelled: "bg-gray-100 text-gray-500",
  failed: "bg-red-100 text-red-700",
};

const TERMINAL_STATUSES: CampaignStatus[] = ["sent", "cancelled", "failed"];

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const c = await getCampaign(id);
      setCampaign(c);
      setError("");
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || "Failed to load"
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while status is non-terminal (and especially fast while sending).
  useEffect(() => {
    if (!campaign) return;
    if (TERMINAL_STATUSES.includes(campaign.status)) return;
    const interval = campaign.status === "sending" ? 3_000 : 15_000;
    const t = setInterval(load, interval);
    return () => clearInterval(t);
  }, [campaign, load]);

  async function runAction(
    label: string,
    fn: () => Promise<unknown>,
    confirmText?: string
  ) {
    if (confirmText && !window.confirm(confirmText)) return;
    setActionLoading(label);
    setError("");
    try {
      await fn();
      await load();
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || `${label} failed`
      );
    } finally {
      setActionLoading(null);
    }
  }

  if (loading && !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }
  if (!campaign) {
    return (
      <div className="min-h-screen p-8">
        <div className="bg-red-50 text-red-700 p-4 rounded-md">
          {error || "Campaign not found"}
        </div>
        <Link
          to="/admin/campaigns"
          className="text-purple-700 hover:underline text-sm mt-4 inline-block"
        >
          ← Back to list
        </Link>
      </div>
    );
  }

  const audience = decodeAudienceSpec(campaign);
  const payload = decodePayload(campaign);
  const target = campaign.target_count ?? 0;
  const sent = campaign.sent_count ?? 0;
  const failed = campaign.failed_count ?? 0;
  const pct = target > 0 ? Math.min(100, Math.round(((sent + failed) / target) * 100)) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-10 px-6">
        <Link
          to="/admin/campaigns"
          className="text-sm text-purple-700 hover:underline"
        >
          ← Back to campaigns
        </Link>

        <div className="mt-3 flex justify-between items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              {campaign.name}
            </h1>
            <div className="text-sm text-gray-500 font-mono mt-1">
              {campaign.campaign_id}
            </div>
          </div>
          <span
            className={`px-3 py-1 text-sm font-medium rounded ${STATUS_BADGE[campaign.status]}`}
          >
            {campaign.status}
          </span>
        </div>

        {/* Action bar */}
        <div className="mt-6 flex flex-wrap gap-2">
          {campaign.status === "draft" && (
            <>
              <button
                onClick={() => nav(`/admin/campaigns/${campaign.campaign_id}/edit`)}
                disabled={actionLoading !== null}
                className="bg-white border border-purple-300 text-purple-700 px-4 py-2 rounded-md hover:bg-purple-50"
              >
                Edit
              </button>
              <button
                onClick={() =>
                  runAction(
                    "approve",
                    () => approveCampaign(campaign.campaign_id),
                    `Approve this campaign? Audience will be resolved now and frozen as target_count. Campaign will fire at ${campaign.scheduled_at}.`
                  )
                }
                disabled={actionLoading !== null}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-gray-300"
              >
                {actionLoading === "approve" ? "Approving…" : "Approve"}
              </button>
              <button
                onClick={() =>
                  runAction(
                    "delete",
                    async () => {
                      await deleteCampaign(campaign.campaign_id);
                      nav("/admin/campaigns");
                    },
                    "Delete this draft campaign? This cannot be undone."
                  )
                }
                disabled={actionLoading !== null}
                className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded-md hover:bg-red-50"
              >
                Delete draft
              </button>
            </>
          )}
          {campaign.status === "approved" && (
            <>
              <button
                onClick={() =>
                  runAction(
                    "hold",
                    () => holdCampaign(campaign.campaign_id),
                    "Hold this approved campaign? Dispatcher will skip it until you Resume."
                  )
                }
                disabled={actionLoading !== null}
                className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 disabled:bg-gray-300"
              >
                {actionLoading === "hold" ? "Holding…" : "Hold"}
              </button>
              <button
                onClick={() =>
                  runAction(
                    "cancel",
                    () => cancelCampaign(campaign.campaign_id),
                    "Cancel this approved campaign? It won't fire. This is permanent."
                  )
                }
                disabled={actionLoading !== null}
                className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded-md hover:bg-red-50"
              >
                Cancel
              </button>
            </>
          )}
          {campaign.status === "held" && (
            <>
              <button
                onClick={() =>
                  runAction("resume", () => resumeCampaign(campaign.campaign_id))
                }
                disabled={actionLoading !== null}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
              >
                {actionLoading === "resume" ? "Resuming…" : "Resume"}
              </button>
              <button
                onClick={() =>
                  runAction(
                    "cancel",
                    () => cancelCampaign(campaign.campaign_id),
                    "Cancel this held campaign?"
                  )
                }
                disabled={actionLoading !== null}
                className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded-md hover:bg-red-50"
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Progress card — shows once target_count is set (i.e. post-approve) */}
        {target > 0 && (
          <section className="mt-8 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-3">Delivery progress</h2>
            <div className="w-full h-3 bg-gray-200 rounded overflow-hidden">
              <div
                className="h-full bg-purple-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-2 text-sm text-gray-600 flex justify-between">
              <span>
                {sent + failed} / {target} dispatched
                {failed > 0 && (
                  <span className="text-red-600 ml-2">
                    · {failed} failed
                  </span>
                )}
              </span>
              <span className="font-mono">{pct}%</span>
            </div>
            {campaign.status === "sending" && (
              <div className="mt-2 text-xs text-gray-500">
                Polling every 3s while dispatching…
              </div>
            )}
          </section>
        )}

        {/* Details */}
        <section className="mt-6 bg-white rounded-lg shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold">Details</h2>
          <Row label="Merchant" value={campaign.merchant_id} />
          <Row label="Platform" value={campaign.platform} />
          <Row label="Scheduled at" value={fmtTime(campaign.scheduled_at)} />
          <Row label="Created" value={`${fmtTime(campaign.created_at)} by ${campaign.created_by}`} />
          {campaign.started_at && <Row label="Started" value={fmtTime(campaign.started_at)} />}
          {campaign.completed_at && <Row label="Completed" value={fmtTime(campaign.completed_at)} />}
          {campaign.last_error && (
            <Row
              label="Last error"
              value={<span className="text-red-700 text-sm">{campaign.last_error}</span>}
            />
          )}
        </section>

        {/* Audience */}
        <section className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-3">Audience</h2>
          <div className="text-sm text-gray-700">
            <div>
              Type:{" "}
              <span className="font-mono">{campaign.audience_type}</span>
            </div>
            <pre className="mt-2 bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-auto">
              {JSON.stringify(audience, null, 2)}
            </pre>
          </div>
        </section>

        {/* Payload */}
        <section className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-3">Pass payload</h2>
          <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-auto">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </section>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline gap-3 text-sm">
    <div className="w-32 text-gray-500">{label}</div>
    <div className="flex-1 text-gray-800 font-mono">{value}</div>
  </div>
);

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default CampaignDetail;
